import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";

const SLIDE_IMAGES = [
  "https://images.unsplash.com/photo-1760450117431-5c70c2cc62aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
  "https://images.unsplash.com/photo-1774535140090-8f860e44dd0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
  "https://images.unsplash.com/photo-1759745125627-333e78bc1edc?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
];

export default function Hero() {
  const { t, dir, lang } = useI18n();
  const slides = t.hero.slides;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(id);
  }, [slides.length]);

  const slide = slides[index];
  const isRtl = dir === "rtl";

  return (
    <section className="relative h-screen min-h-[720px] w-full overflow-hidden bg-secondary" data-testid="hero-section">
      <AnimatePresence mode="sync">
        <motion.div
          key={`${lang}-${index}`}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
        >
          <div
            className="absolute inset-0 ken-burns bg-cover bg-center"
            style={{ backgroundImage: `url('${SLIDE_IMAGES[index % SLIDE_IMAGES.length]}')` }}
          />
          <div className={`absolute inset-0 ${isRtl ? "bg-gradient-to-l from-black/85 via-black/55 to-black/30" : "bg-gradient-to-r from-black/85 via-black/55 to-black/30"}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      <div className="relative h-full container-page flex flex-col justify-end pb-24 pt-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${lang}-${index}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-6" data-testid="hero-eyebrow">
              <span className="h-px w-12 bg-primary" />
              <span className="overline text-primary">{slide.eyebrow}</span>
            </div>
            <h1
              className="text-5xl sm:text-6xl lg:text-8xl font-black text-white leading-[0.95] tracking-tight"
              data-testid="hero-title"
            >
              {slide.title.map((line, i) => (
                <motion.span
                  key={`${lang}-${index}-${i}`}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.12, duration: 0.7, ease: "easeOut" }}
                  className="block"
                >
                  {line}
                </motion.span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-8 max-w-2xl text-white/80 text-base sm:text-lg leading-relaxed"
              data-testid="hero-subtitle"
            >
              {slide.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.6 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Button
                size="lg"
                className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-8"
                onClick={() => (window.location.href = "#contact")}
                data-testid="hero-cta-primary"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className={`h-4 w-4 ${isRtl ? "mr-2 rotate-180" : "ml-2"}`} />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-sm h-12 px-8 bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary"
                onClick={() => (window.location.href = "#projects")}
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        <div
          className={`absolute bottom-10 flex items-center gap-3 ${
            isRtl ? "left-6 sm:left-12" : "right-6 sm:right-12"
          }`}
          data-testid="hero-indicators"
        >
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-px transition-all duration-500 ${
                i === index ? "w-16 bg-primary" : "w-8 bg-white/40 hover:bg-white/70"
              }`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-indicator-${i}`}
            />
          ))}
          <span className={`text-xs text-white/70 tabular-nums tracking-widest ${isRtl ? "mr-3" : "ml-3"}`} data-testid="hero-counter">
            {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}
